import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface PaywallStatus {
  allowed: boolean;
  reason: "ok" | "trial_expired" | "limit_reached" | "suspended" | "no_org";
  billingStatus: string;
  trialDaysLeft: number | null;
  graceDaysLeft: number | null;
  usagePct: number;
}

export function usePaywall() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["paywall-status", user?.id],
    queryFn: async (): Promise<PaywallStatus> => {
      if (!user?.id) return { allowed: false, reason: "no_org", billingStatus: "none", trialDaysLeft: null, graceDaysLeft: null, usagePct: 0 };

      // Get org
      const { data: member } = await supabase
        .from("org_members")
        .select("org_id")
        .eq("user_id", user.id)
        .limit(1)
        .maybeSingle();

      if (!member) return { allowed: false, reason: "no_org", billingStatus: "none", trialDaysLeft: null, graceDaysLeft: null, usagePct: 0 };

      const { data: org } = await supabase
        .from("organizations")
        .select("billing_status, trial_ends_at, grace_period_ends_at, monthly_message_limit, monthly_ai_calls_limit, plan")
        .eq("id", member.org_id)
        .single();

      if (!org) return { allowed: false, reason: "no_org", billingStatus: "none", trialDaysLeft: null, graceDaysLeft: null, usagePct: 0 };

      const now = new Date();
      const billingStatus = (org as any).billing_status || "trialing";
      const trialEndsAt = (org as any).trial_ends_at ? new Date((org as any).trial_ends_at) : null;
      const graceEndsAt = (org as any).grace_period_ends_at ? new Date((org as any).grace_period_ends_at) : null;

      const trialDaysLeft = trialEndsAt ? Math.max(0, Math.ceil((trialEndsAt.getTime() - now.getTime()) / 86400000)) : null;
      const graceDaysLeft = graceEndsAt ? Math.max(0, Math.ceil((graceEndsAt.getTime() - now.getTime()) / 86400000)) : null;

      // Check usage
      const periodKey = now.toISOString().slice(0, 7);
      const { data: usage } = await supabase
        .from("usage_tracking")
        .select("metric, value")
        .eq("org_id", member.org_id)
        .eq("period_key", periodKey);

      const msgsUsed = usage?.find(u => u.metric === "messages")?.value ?? 0;
      const aiUsed = usage?.find(u => u.metric === "ai_calls")?.value ?? 0;

      const msgPct = org.monthly_message_limit ? Math.round((msgsUsed / org.monthly_message_limit) * 100) : 0;
      const aiPct = org.monthly_ai_calls_limit ? Math.round((aiUsed / org.monthly_ai_calls_limit) * 100) : 0;
      const usagePct = Math.max(msgPct, aiPct);

      // Determine if allowed
      if (billingStatus === "suspended") {
        return { allowed: false, reason: "suspended", billingStatus, trialDaysLeft, graceDaysLeft, usagePct };
      }

      if (billingStatus === "trialing" && trialEndsAt && now > trialEndsAt) {
        return { allowed: false, reason: "trial_expired", billingStatus, trialDaysLeft: 0, graceDaysLeft, usagePct };
      }

      if (billingStatus === "past_due" && graceEndsAt && now > graceEndsAt) {
        return { allowed: false, reason: "suspended", billingStatus: "suspended", trialDaysLeft, graceDaysLeft: 0, usagePct };
      }

      if (usagePct >= 100) {
        return { allowed: false, reason: "limit_reached", billingStatus, trialDaysLeft, graceDaysLeft, usagePct };
      }

      return { allowed: true, reason: "ok", billingStatus, trialDaysLeft, graceDaysLeft, usagePct };
    },
    enabled: !!user?.id,
    staleTime: 60_000,
  });
}
